import React, { useEffect, useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    Building2,
    MapPin,
    Box,
    FolderKanban,
    CheckCircle2,
    Clock,
    PauseCircle,
    Loader2,
    AlertCircle,
} from "lucide-react";
import { getEnquiries } from  "../service/userService";

// Readable labels for the enum-style values the API returns
const STAGE_LABELS = {
    IDEA: "Idea",
    DESIGN: "Design",
    PROTOTYPE: "Prototype",
    PRODUCTION: "Production",
};

const STATUS_STYLES = {
    Idea: "bg-purple-100 text-purple-700",
    Design: "bg-orange-100 text-orange-700",
    Prototype: "bg-blue-100 text-blue-700",
    Production: "bg-green-100 text-green-700",
};

function humanize(value) {
    if (!value) return "—";
    return value
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

// Transforms a raw enquiry record from the API into the shape the table expects.
// NOTE: the API has no org/email/status fields — repurposed to fields that
// actually exist: productCategory, industrySector, developmentStage.
function mapEnquiryToProject(enquiry) {
    return {
        id: enquiry.id,
        name: enquiry.projectName || "Untitled Project",
        category: humanize(enquiry.productCategory),
        industrySector: humanize(enquiry.industrySector),
        location: enquiry.preferredLocation || "—",
        regNo: `ENQ-${String(enquiry.id).padStart(4, "0")}`,
        stage: STAGE_LABELS[enquiry.developmentStage] || humanize(enquiry.developmentStage) || "—",
    };
}

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [locationFilter, setLocationFilter] = useState("All Locations");
    const [stageFilter, setStageFilter] = useState("All Stages");

    // ===== Fetch enquiries on mount =====
    useEffect(() => {
        const fetchEnquiries = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getEnquiries();
                const rawList = Array.isArray(response) ? response : response?.data || [];

                setProjects(rawList.map(mapEnquiryToProject));
            } catch (err) {
                console.error("Failed to fetch enquiries:", err);
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load projects. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchEnquiries();
    }, []);

    const locations = useMemo(
        () => ["All Locations", ...new Set(projects.map((p) => p.location))],
        [projects]
    );
    const stages = useMemo(
        () => ["All Stages", ...new Set(projects.map((p) => p.stage))],
        [projects]
    );

    const counts = useMemo(() => {
        const design = projects.filter((p) => p.stage === "Design").length;
        const prototype = projects.filter((p) => p.stage === "Prototype").length;
        const production = projects.filter((p) => p.stage === "Production").length;
        return { total: projects.length, design, prototype, production };
    }, [projects]);

    const filtered = useMemo(() => {
        return projects.filter((p) => {
            const matchesQuery =
                query.trim() === "" ||
                [p.name, p.category, p.industrySector].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesLocation =
                locationFilter === "All Locations" || p.location === locationFilter;
            const matchesStage =
                stageFilter === "All Stages" || p.stage === stageFilter;
            return matchesQuery && matchesLocation && matchesStage;
        });
    }, [projects, query, locationFilter, stageFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track every prototyping project registered across Andhra Pradesh
                    </p>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <AlertCircle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={FolderKanban} label="Total projects" value={loading ? "—" : counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={Clock} label="In Design" value={loading ? "—" : counts.design} tone="bg-orange-50 text-orange-500" />
                    <StatCard icon={CheckCircle2} label="Prototype" value={loading ? "—" : counts.prototype} tone="bg-blue-50 text-blue-500" />
                    <StatCard icon={PauseCircle} label="Production" value={loading ? "—" : counts.production} tone="bg-green-50 text-green-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Registered projects</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {loading ? "Loading projects..." : `${filtered.length} of ${projects.length} projects`}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name, category..."
                                    disabled={loading}
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white disabled:opacity-50"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={locationFilter}
                                    onChange={(e) => setLocationFilter(e.target.value)}
                                    disabled={loading}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300 disabled:opacity-50"
                                >
                                    {locations.map((loc) => (
                                        <option key={loc} value={loc}>
                                            {loc}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={stageFilter}
                                    onChange={(e) => setStageFilter(e.target.value)}
                                    disabled={loading}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300 disabled:opacity-50"
                                >
                                    {stages.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-1/4 px-6 py-3 font-medium">Project Name</th>
                                    <th className="w-1/5 px-6 py-3 font-medium">Category</th>
                                    <th className="w-1/5 px-6 py-3 font-medium">Industry Sector</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Location</th>
                                    {/* <th className="w-[16%] px-6 py-3 font-medium">Enquiry No.</th> */}
                                    <th className="w-[10%] px-6 py-3 font-medium">Stage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 size={18} className="animate-spin" />
                                                Loading projects...
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading && filtered.map((p, idx) => (
                                    <tr
                                        key={p.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""
                                            }`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
                                                    <Box className="h-4 w-4" />
                                                </span>
                                                <span className="font-semibold text-gray-900">{p.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{p.category}</td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{p.industrySector}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                                                {p.location}
                                            </div>
                                        </td>
                                        {/* <td className="px-6 py-4 text-gray-500">{p.regNo}</td> */}
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[p.stage] || "bg-gray-100 text-gray-600"
                                                    }`}
                                            >
                                                {p.stage}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {!loading && !error && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No projects match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}